import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Injector } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule } from '@angular/forms';
import { RouterModule, UrlSerializer } from '@angular/router';
import { AtlasModule } from '@vendasta/atlas';
import { ElementZoneStrategyFactory } from 'elements-zone-strategy';
import { BcNavItemComponent } from './bc-nav-item/bc-nav-item.component';
import { BcPinnedItemsComponent } from './bc-pinned-items/bc-pinned-items.component';
import { BusinessSidebarComponent } from './business-sidebar/business-sidebar.component';
import { BusinessNavChipComponent } from './core-components/chip/chip.component';
import { EncodeBrandNamePipe } from './core-components/encode-brand.pipe';
import { ButtonComponent } from './core-components/form-controls/button/button.component';
import { SearchBoxComponent } from './core-components/form-controls/search-box/search-box.component';
import { TextareaComponent } from './core-components/form-controls/text-area/textarea.component';
import { IconComponent } from './core-components/icon/icon.component';
import { LoaderComponent } from './core-components/loader/loader.component';
import { ModalContentComponent } from './core-components/modal/modal-content.component';
import { ModalFooterComponent } from './core-components/modal/modal-footer.component';
import { ModalHeaderComponent } from './core-components/modal/modal-header.component';
import { ModalComponent } from './core-components/modal/modal.component';
import { ModalService } from './core-components/modal/modal.service';
import { NavItemContainerComponent } from './core-components/nav-item-container/nav-item-container.component';
import { NavItemSectionComponent } from './core-components/nav-item-section/nav-item-section.component';
import { NavItemComponent } from './core-components/nav-item/nav-item.component';
import { SidebarComponent } from './core-components/sidebar/sidebar.component';
import { SnackbarComponent } from './core-components/snackbar/snackbar.component';
import { AtlasTooltipModule } from './core-components/tooltip/tooltip-module';
import { CustomUrlSerializer } from './custom-url-serializer';
import { LocationSelectorComponent } from './location-selector/location-selector.component';
import { ChangeSubscriptionModalComponent } from './modals/change-subscription/change-subscription.modal.component';
import {
    LocationListItemComponent
} from './modals/location-switcher-modal/location-list/location-list-item/location-list-item.component';
import { LocationListComponent } from './modals/location-switcher-modal/location-list/location-list.component';
import { LocationSwitcherModalComponent } from './modals/location-switcher-modal/location-switcher-modal.component';
import { TabHeaderComponent } from './modals/location-switcher-modal/tab-header/tab-header.component';
import { LocationTabComponent } from './modals/location-switcher-modal/tab/tab.component';
import { SalesInfoModalComponent } from './modals/sales-info/sales-info.modal.component';
import { UserRequiredModalComponent } from './modals/user-required/user-required.modal.component';
import { NavigationComponent } from './navigation/navigation.component';
import { AtlasTranslatePipe } from './translate/translate.pipe';

// ************ ************ ************ ************ ************ ************ ************ ************ ************ ************
// THESE ARE DECLARED HERE SO THAT WE PREVENT DUPLICATION OF CODE. ALSO TO PREVENT FORGETTING TO UPDATE EVERYWHERE THAT IT'S NEEDED.
// ************ ************ ************ ************ ************ ************ ************ ************ ************ ************

export const MODULE_DECLARATIONS = [
    NavigationComponent,
    SidebarComponent,
    NavItemComponent,
    IconComponent,
    BusinessSidebarComponent,
    NavItemContainerComponent,
    NavItemSectionComponent,
    BcNavItemComponent,
    BcPinnedItemsComponent,
    ModalComponent,
    SalesInfoModalComponent,
    UserRequiredModalComponent,
    ChangeSubscriptionModalComponent,
    TextareaComponent,
    ModalHeaderComponent,
    ModalContentComponent,
    ModalFooterComponent,
    ButtonComponent,
    LocationSelectorComponent,
    LocationSwitcherModalComponent,
    SearchBoxComponent,
    LocationTabComponent,
    LocationListComponent,
    LocationListItemComponent,
    EncodeBrandNamePipe,
    AtlasTranslatePipe,
    LoaderComponent,
    SnackbarComponent,
    BusinessNavChipComponent,
    TabHeaderComponent,
];

export const MODULE_PROVIDERS = [
    {
        provide: UrlSerializer,
        useClass: CustomUrlSerializer,
    },
    ModalService
];

export const MODULE_IMPORTS = [
    CommonModule,
    RouterModule,
    FormsModule,
    AtlasModule,
    DragDropModule,
    AtlasTooltipModule,
];

export const MODULE_EXPORTS = [
    NavigationComponent,
    NavItemComponent,
    NavItemContainerComponent,
    AtlasModule,
];

export const MODULE_ENTRY_COMPONENTS = [
    NavigationComponent,
    NavItemComponent,
    NavItemContainerComponent,
    BusinessSidebarComponent,
    SalesInfoModalComponent,
    UserRequiredModalComponent,
    ChangeSubscriptionModalComponent,
    LocationSwitcherModalComponent,
    SnackbarComponent,
];

// ************ Elements-specific ************ //

export function DEFINE_CUSTOM_ELEMENTS(injector: Injector): void {
    try {
        const navbarStrategy = new ElementZoneStrategyFactory(NavigationComponent, injector);
        const navbar = createCustomElement(NavigationComponent, {
            injector: injector,
            strategyFactory: navbarStrategy
        });
        customElements.define('atlas-business-navbar-element', navbar);

        const itemStrategy = new ElementZoneStrategyFactory(NavItemComponent, injector);
        const item = createCustomElement(NavItemComponent, { injector: injector, strategyFactory: itemStrategy });
        customElements.define('atlas-nav-item-element', item);

        const itemContainerStrategy = new ElementZoneStrategyFactory(NavItemContainerComponent, injector);
        const itemContainer = createCustomElement(NavItemContainerComponent, {
            injector: injector,
            strategyFactory: itemContainerStrategy
        });
        customElements.define('atlas-nav-item-container-element', itemContainer);
    } catch (e) {
        console.log(`failed to register web elements, you may need the @webcomponents/custom-elements polyfill: ${e.message}`);
    }
}
